import { ErrorBoundary } from "@sentry/react";
import React, { Dispatch, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { setParameters, setSpecificParameter } from "../../../actions";
import {
  ALL_OPTION_NO_SPACE,
  DEFAULT_BRAND_PERFORMANCE_DATE_PARAMS,
  DEFAULT_BRAND_PERFORMANCE_PARAMETERS,
  performancePages,
} from "../../../constants";
import { FilterContext } from "../../../context";
import { eventTracking, MixpanelEvents } from "../../../utils/userTracking";
import { currentDefaultFiltersSet, defaultFiltersSet } from "../../../utils/utilityFunctions";
import { ErrorMsg } from "../../AppMessages";
import { FiltersLayout } from "../../Layouts";
import { DefaultFilter, MultiSelectFilter } from "../common";
import BrandKPIMaximumScoreRangeFilter from "../common/BrandKPITrendMaximumScoreFilter";
import InAppSingleSelectNestedFilter from "../common/InAppSingleSelectNestedFilter";
import PurchaseFunnelDateFilter from "../common/PurchaseFunnelDateFilter";
import { closeFilterOptions } from "../subs/helpers";

interface Props {
  name?: string;
  granularityOptions?: JSX.Element | null;
  activePage: performancePages;
  lastRefreshDate?: string;
  brandKPIMaxRangeScore?: number;
  setBrandKPIMaxRangeScore?: Dispatch<React.SetStateAction<number>>;
  nimLatestRanking?: number;
  setNimLatestRanking?: Dispatch<React.SetStateAction<number>>;
  availableDataDateRange: { min_date: string; max_date: string };
  availableGranularities: Array<string>;
}

const BrandPerformanceFilters = withRouter((props: RouteComponentProps & Props) => {
  const dispatch = useDispatch();
  const { showFilterOptions, setShowFilterOptions } = useContext(FilterContext);

  const {
    history,
    granularityOptions,
    activePage,
    lastRefreshDate,
    brandKPIMaxRangeScore,
    setBrandKPIMaxRangeScore,
    availableDataDateRange,
    setNimLatestRanking,
    nimLatestRanking,
    availableGranularities,
  } = props;

  const {
    date_range: dateRangeParam,
    brand: brandParam,
    market: marketParam,
    model: modelParam,
    category: categoryParam,
    sub_category: subCategoryParam,
    attribute: attributeParam,
    competitor: competitorParam,
    date_range_brand_perf: dateRangePerfParam,
    granularity: granularityParam,
    nissan_competitor: nissanCompetitorParam,
    performance_page: performancePageParam,
    segment: segmentParam,
    brand_kpi: brandKpiParam,
    annual_mileage: annualMilageParam,
    mo: moParam,
    model_year: modelYearParam,
    data_category: dataCategoryParam,
    select_value: selectValueParam,
    ranking: rankingParam,
    data_source: dataSourceParam,
  } = useSelector((state: RootStateOrAny) => state.parameters);

  const isUSMarket = marketParam?.toLowerCase() === "united states" || marketParam?.toLowerCase() === "infiniti_usa";

  useEffect(() => {
    const setDefaultFilters = !defaultFiltersSet("Brand Performance");
    if (setDefaultFilters) {
      dispatch(setParameters(DEFAULT_BRAND_PERFORMANCE_PARAMETERS));
      currentDefaultFiltersSet("Brand Performance");
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();

    params.set("date_range", dateRangeParam);
    params.set("brand", brandParam);
    params.set("market", marketParam);
    params.set("model", modelParam);
    params.set("granularity", granularityParam);
    params.set("category", categoryParam);
    params.set("sub_category", subCategoryParam);
    params.set("attribute", attributeParam);
    params.set("competitor", competitorParam);
    params.set("nissan_competitor", nissanCompetitorParam);
    params.set("date_range_brand_perf", dateRangePerfParam);
    params.set("performance_page", activePage);
    params.set("segment", segmentParam);
    params.set("brand_kpi", brandKpiParam);
    params.set("annual_mileage", annualMilageParam);
    params.set("mo", moParam);
    params.set("model_year", modelYearParam);
    params.set("data_category", dataCategoryParam);
    params.set("select_value", selectValueParam);
    params.set("ranking", rankingParam);
    params.set("data_source", dataSourceParam);
    history.push({ search: `?${params.toString()}` });
  }, [
    history,
    dateRangeParam,
    brandParam,
    marketParam,
    nissanCompetitorParam,
    dateRangePerfParam,
    granularityParam,
    competitorParam,
    categoryParam,
    subCategoryParam,
    attributeParam,
    performancePageParam,
    modelParam,
    activePage,
    segmentParam,
    brandKpiParam,
    annualMilageParam,
    moParam,
    modelYearParam,
    dataCategoryParam,
    selectValueParam,
    rankingParam,
    dataSourceParam,
  ]);

  const volume_filters = useSelector((state: RootStateOrAny) => state.filters.brand_performance_volume_filters);
  const oao_filters = useSelector((state: RootStateOrAny) => state.filters.brand_performance_oao_filters);
  const brand_health_index_filters = useSelector((state: RootStateOrAny) => state.filters.brand_health_index_filters);
  const buzz_tracking_filters = useSelector((state: RootStateOrAny) => state.filters.buzz_tracking_filters);
  const image_attribute_filters = useSelector((state: RootStateOrAny) => state.filters.brand_performance_image_attribute_filters);
  const brand_segment_volume_filters = useSelector((state: RootStateOrAny) => state.filters.brand_segment_volume_filters);
  const brand_market_share_filters = useSelector((state: RootStateOrAny) => state.filters.brand_market_share_filters);
  const sales_by_volume_filters = useSelector((state: RootStateOrAny) => state.filters.brand_sales_by_volume_filters);
  const brand_kpi_trend_filters = useSelector((state: RootStateOrAny) => state.filters.brand_kpi_trend_filters);
  const brand_kpi_summary_filters = useSelector((state: RootStateOrAny) => state.filters.brand_kpi_summary_filters);
  const sov_som_filters = useSelector((state: RootStateOrAny) => state.filters.brand_performance_sov_som_filters);
  const image_snapshot_filters = useSelector((state: RootStateOrAny) => state.filters.brand_performance_image_snapshot_filters);
  const nim_filters = useSelector((state: RootStateOrAny) => state.filters.brand_performance_nim_filters);
  const nim_latest_filters = useSelector((state: RootStateOrAny) => state.filters.brand_performance_nim_latest_filters);
  const rv_filters = useSelector((state: RootStateOrAny) => state.filters.brand_performance_rv_filters);
  const tpva_filters = useSelector((state: RootStateOrAny) => state.filters.brand_performance_tpva_filters);
  const tp_filters = useSelector((state: RootStateOrAny) => state.filters.brand_performance_tp_filters);

  const monthly_table_for_download_filters = useSelector(
    (state: RootStateOrAny) => state.filters.brand_performance_monthly_table_for_download_filters
  );
  const yearly_table_for_download_filters = useSelector(
    (state: RootStateOrAny) => state.filters.brand_performance_yearly_table_for_download_filters
  );

  const quarterly_table_for_download_filters = useSelector(
    (state: RootStateOrAny) => state.filters.brand_performance_quarterly_table_for_download_filters
  );

  const purchase_funnel_filters = useSelector((state: RootStateOrAny) => state.filters.brand_performance_purchase_funnel_filters);
  const tech_awareness_filters = useSelector((state: RootStateOrAny) => state.filters.brand_performance_tech_awareness_filters);
  const [competitorValue, setCompetitorValue] = useState("");
  const [segmentValue, setSegmentValue] = useState("");
  const [annualMileageValue, setAnnualMileageValue] = useState("");
  const [moValue, setMoValue] = useState("");
  const [modelYearValue, setModelYearValue] = useState("");
  const [dataCategoryValue, setDataCategoryValue] = useState("");
  const [modelValue, setModelValue] = useState("");
  const [marketValue, setMarketValue] = useState("");
  const [attributeValue, setAttributeValue] = useState("");
  const [defaultDateInitialised, setDefaultDateInitialised] = useState(false);

  const filters = useMemo(() => {
    if (activePage === "volume") return volume_filters;
    if (activePage === "oao") return oao_filters;
    if (activePage === "brand-health-index") return brand_health_index_filters;
    if (activePage === "buzz-tracking") return buzz_tracking_filters;
    if (activePage === "image-attributes") return image_attribute_filters;
    if (activePage === "segment-volume-tiv") return brand_segment_volume_filters;
    if (activePage === "brand-market-share") return brand_market_share_filters;
    if (activePage === "market-share-by-channel") return sales_by_volume_filters;
    if (activePage === "brand-kpi-trend") return brand_kpi_trend_filters;
    if (activePage === "brand-kpi-summary") return brand_kpi_summary_filters;
    if (activePage === "sov/som") return sov_som_filters;
    if (activePage === "purchase-funnel") return purchase_funnel_filters;
    if (activePage === "tech-awareness") return tech_awareness_filters;
    if (activePage === "image-snapshot") return image_snapshot_filters;
    if (activePage === "monthly-table-for-download") return monthly_table_for_download_filters;
    if (activePage === "yearly-table-for-download") return yearly_table_for_download_filters;
    if (activePage === "quarterly-table-for-download") return quarterly_table_for_download_filters;
    if (activePage === "nim") return nim_filters;
    if (activePage === "nim-latest") return nim_latest_filters;
    if (activePage === "rv" || activePage === "rv-table-for-download") return rv_filters;
    if (activePage === "tpva") return tpva_filters;
    if (activePage === "tp") return tp_filters;
    return volume_filters;
  }, [
    volume_filters,
    oao_filters,
    brand_health_index_filters,
    buzz_tracking_filters,
    image_attribute_filters,
    brand_segment_volume_filters,
    brand_market_share_filters,
    sales_by_volume_filters,
    brand_kpi_trend_filters,
    brand_kpi_summary_filters,
    sov_som_filters,
    nim_filters,
    purchase_funnel_filters,
    tech_awareness_filters,
    image_snapshot_filters,
    monthly_table_for_download_filters,
    yearly_table_for_download_filters,
    quarterly_table_for_download_filters,
    nim_latest_filters,
    rv_filters,
    tpva_filters,
    tp_filters,
    performancePageParam,
    activePage,
  ]);

  const stripFY = useCallback((label: string) => label.replace(/\bFY\s+/g, ""), []);

  // Helper function to convert FY to CY for display only (China, China_Ev, China_Phev markets)
  const convertDisplayLabel = useCallback(
    (label: string) => {
      if ((marketParam === "China" || marketParam === "China_Ev" || marketParam === "China_Phev") && label) {
        return label.replace(/FY/g, "CY");
      }
      return label;
    },
    [marketParam]
  );

  const groupedDateRangeData = useMemo(() => {
    if (filters?.fiscal_year?.length) {
      return filters?.fiscal_year?.map((val: { fiscal_year: number; quarters: string[]; months?: string[] }) => ({
        year: `FY ${val?.fiscal_year}`,
        child: "months" in val ? val.months?.map((val) => ({ month: val })) : val.quarters?.map((val) => ({ quarter: val })),
      }));
    }
    return [];
  }, [filters?.fiscal_year, activePage]);

  // Transform date range data for display (show CY for China, China_Ev, China_Phev, but keep FY internally)
  const displayDateRangeData = useMemo(() => {
    if (marketParam !== "China" && marketParam !== "China_Ev" && marketParam !== "China_Phev") return groupedDateRangeData;

    return groupedDateRangeData?.map((item: any) => ({
      ...item,
      year: convertDisplayLabel(item.year), // Convert parent year from FY to CY
      child: item.child?.map((childItem: any) => {
        const key = Object.keys(childItem)[0];
        return {
          [key]: convertDisplayLabel(childItem[key]), // Convert child quarters/months from FY to CY
        };
      }),
    }));
  }, [groupedDateRangeData, marketParam, convertDisplayLabel]);

  useEffect(() => {
    if (activePage == "nim-latest" || activePage == "quarterly-table-for-download") {
      setTimeout(() => dispatch(setSpecificParameter("category", "Total")), 5);
    }
    dispatch(setSpecificParameter("competitor", ALL_OPTION_NO_SPACE));
    setDefaultDateInitialised(false);
  }, [activePage]);

  useEffect(() => {
    const isDownloadPage =
      activePage == "monthly-table-for-download" ||
      activePage == "yearly-table-for-download" ||
      activePage == "quarterly-table-for-download" ||
      activePage == "rv-table-for-download";

    if (Array.isArray(filters?.market) && !isDownloadPage) {
      if (filters.market.length && !filters.market.includes(marketParam))
        dispatch(setSpecificParameter("market", filters.market[0]));
      else if (!filters.market.length)
        dispatch(setSpecificParameter("market", ALL_OPTION_NO_SPACE));
    }

    const isModelDownloadPage =
      activePage == "yearly-table-for-download" ||
      activePage == "monthly-table-for-download" ||
      activePage == "quarterly-table-for-download";

    if (Array.isArray(filters?.model) && !isModelDownloadPage) {
      if (filters.model.length && !filters.model.includes(modelParam))
        dispatch(setSpecificParameter("model", filters.model[0]));
      else if (!filters.model.length)
        dispatch(setSpecificParameter("model", ALL_OPTION_NO_SPACE));
    }

    if (Array.isArray(filters?.category)) {
      if (filters.category.length && !filters.category.includes(categoryParam))
        dispatch(setSpecificParameter("category", filters.category[0]));
      else if (!filters.category.length)
        dispatch(setSpecificParameter("category", ALL_OPTION_NO_SPACE));
    }

    if (Array.isArray(filters?.sub_category)) {
      if (filters.sub_category.length && !filters.sub_category.includes(subCategoryParam))
        dispatch(setSpecificParameter("sub_category", filters.sub_category[0]));
      else if (!filters.sub_category.length)
        dispatch(setSpecificParameter("sub_category", ALL_OPTION_NO_SPACE));
    }

    if (Array.isArray(filters?.attribute)) {
      const isQuarterlyImageOrNIM =
        activePage == "quarterly-table-for-download" &&
        (dataCategoryParam == "Image" || dataCategoryParam == "NIM");

      if (!isQuarterlyImageOrNIM) {
        if (
          ((activePage == "quarterly-table-for-download" && dataCategoryParam != "Image" && dataCategoryParam != "NIM") ||
            activePage != "quarterly-table-for-download") &&
          filters.attribute.length &&
          !filters.attribute.includes(decodeURIComponent(attributeParam))
        )
          dispatch(setSpecificParameter("attribute", filters.attribute[0]));
        else if (!filters.attribute.length)
          dispatch(setSpecificParameter("attribute", ALL_OPTION_NO_SPACE));
      } else if (isQuarterlyImageOrNIM && filters.attribute.length && attributeParam != ALL_OPTION_NO_SPACE) {
        const selectedItems = attributeParam.split(",");
        if (selectedItems.every((attribute: string) => filters.attribute.includes(attribute))) return;
        const validItems: Array<string> = [];
        selectedItems.forEach((item: string) => {
          return filters.attribute.includes(decodeURIComponent(item)) && validItems.push(item);
        });
        validItems.length > 0
          ? dispatch(setSpecificParameter("attribute", validItems.join(",")))
          : dispatch(setSpecificParameter("attribute", ALL_OPTION_NO_SPACE));
      } else if (isQuarterlyImageOrNIM && !filters.attribute.length) {
        dispatch(setSpecificParameter("attribute", ALL_OPTION_NO_SPACE));
      }
    }

    if (Array.isArray(filters?.brand_kpi)) {
      if (filters.brand_kpi.length && !filters.brand_kpi.includes(brandKpiParam))
        dispatch(setSpecificParameter("brand_kpi", filters.brand_kpi[0]));
      else if (!filters.brand_kpi.length)
        dispatch(setSpecificParameter("brand_kpi", ALL_OPTION_NO_SPACE));
    }

    if (Array.isArray(filters?.select_value)) {
      if (filters.select_value.length && !filters.select_value.includes(selectValueParam))
        dispatch(setSpecificParameter("select_value", filters.select_value[0]));
      else if (!filters.select_value.length)
        dispatch(setSpecificParameter("select_value", ALL_OPTION_NO_SPACE));
    }

    if (Array.isArray(filters?.ranking)) {
      if (filters.ranking.length && !filters.ranking.includes(Number(rankingParam)))
        dispatch(setSpecificParameter("ranking", filters.ranking[0]));
      else if (!filters.ranking.length)
        dispatch(setSpecificParameter("ranking", ALL_OPTION_NO_SPACE));
    }

    if (Array.isArray(filters?.annual_mileage)) {
      if (filters.annual_mileage.length && !filters.annual_mileage.includes(annualMilageParam))
        dispatch(setSpecificParameter("annual_mileage", filters.annual_mileage[0]));
      else if (!filters.annual_mileage.length)
        dispatch(setSpecificParameter("annual_mileage", ALL_OPTION_NO_SPACE));
    }

    if (Array.isArray(filters?.mo)) {
      if (filters.mo.length && !filters.mo.includes(moParam))
        dispatch(setSpecificParameter("mo", filters.mo[0]));
      else if (!filters.mo.length)
        dispatch(setSpecificParameter("mo", ALL_OPTION_NO_SPACE));
    }

    if (Array.isArray(filters?.model_year)) {
      if (filters.model_year.length && !filters.model_year.includes(modelYearParam))
        dispatch(setSpecificParameter("model_year", filters.model_year[0]));
      else if (!filters.model_year.length)
        dispatch(setSpecificParameter("model_year", ALL_OPTION_NO_SPACE));
    }

    if (
      Array.isArray(filters?.competitor) &&
      (activePage == "purchase-funnel" || activePage == "tech-awareness") &&
      !competitorParam?.includes(",")
    ) {
      if (filters.competitor.length && !filters.competitor.includes(competitorParam))
        dispatch(setSpecificParameter("competitor", filters.competitor[0]));
      else if (!filters.competitor.length)
        dispatch(setSpecificParameter("competitor", ALL_OPTION_NO_SPACE));
    }

    if (Array.isArray(filters?.data_category) && !/all/gi.test(dataCategoryParam) && !dataCategoryParam?.includes(",")) {
      if (filters.data_category.length && !filters.data_category.includes(dataCategoryParam))
        dispatch(setSpecificParameter("data_category", filters.data_category[0]));
      else if (!filters.data_category.length)
        dispatch(setSpecificParameter("data_category", ALL_OPTION_NO_SPACE));
    }

    if (Array.isArray(filters?.segment) && !/all/gi.test(segmentParam) && !segmentParam?.includes(",")) {
      if (filters.segment.length && !filters.segment.includes(segmentParam))
        dispatch(setSpecificParameter("segment", filters.segment[0]));
      else if (!filters.segment.length)
        dispatch(setSpecificParameter("segment", ALL_OPTION_NO_SPACE));
    }

  }, [
    dateRangeParam,
    marketParam,
    modelParam,
    categoryParam,
    subCategoryParam,
    attributeParam,
    competitorParam,
    filters,
    dateRangePerfParam,
    performancePageParam,
    segmentParam,
    brandKpiParam,
    dataCategoryParam,
    selectValueParam,
    rankingParam,
    modelYearParam,
    moParam,
    annualMilageParam,
    dataSourceParam,
  ]);

  useEffect(() => {
    const defaultDataSource = isUSMarket ? "yougov" : "Gfk";
    if (dataSourceParam !== defaultDataSource) {
      dispatch(setSpecificParameter("data_source", defaultDataSource));
    }
  }, [marketParam]);

  useEffect(() => {
    const hasDataSourceFilter =
      activePage === "image-attributes" ||
      activePage === "purchase-funnel" ||
      activePage === "image-snapshot" ||
      activePage === "quarterly-table-for-download";

    if (hasDataSourceFilter && isUSMarket) {
      dispatch(setSpecificParameter("data_source", "yougov"));
    }
  }, [activePage, isUSMarket]);

  useEffect(() => {
    if (filters?.fiscal_year?.length && !dateRangePerfParam?.length) {
      const latestYear = filters?.fiscal_year[0];
      const latestYearGroupedData = groupedDateRangeData?.filter((val: { year: any }) => val.year?.includes(latestYear?.toString()))[0];
      const dateParam = latestYearGroupedData?.child?.map((val: { quarter: string }) => `${val?.quarter} FY ${latestYear}`);
      dispatch(setSpecificParameter("date_range_brand_perf", dateParam));
    }
  }, [filters, groupedDateRangeData, dateRangePerfParam]);

  //initialize date_range_brand_perf to last 12 months if granularity monthly or last 6 quarters if granularity quarterly
  useEffect(() => {
    if (!defaultDateInitialised && (activePage == "monthly-table-for-download" || activePage == "rv-table-for-download")) {
      dispatch(setSpecificParameter("date_range_brand_perf", "Last 12 months"));
      setDefaultDateInitialised(true);
      return;
    }
    if (!defaultDateInitialised && availableGranularities.includes("quarterly")) {
      dispatch(setSpecificParameter("date_range_brand_perf", "Last 6 quarters"));
      setDefaultDateInitialised(true);
      return;
    }
    const k = granularityParam == "monthly" ? 4 : 6;
    if (!defaultDateInitialised && filters?.fiscal_year) {
      const values = [];
      for (let i = 0; i < filters.fiscal_year.length; i++) {
        const units = "months" in filters.fiscal_year[i] ? filters.fiscal_year[i].months : filters.fiscal_year[i].quarters;
        for (let j = 0; j < units?.length; j++) {
          if (values.length < k) {
            values.push(`${units[j]} FY ${filters?.fiscal_year[i].fiscal_year}`);
          } else {
            break;
          }
        }

        if (values.length >= k) {
          break;
        }
      }
      dispatch(setSpecificParameter("date_range_brand_perf", values.join(",")));
      setDefaultDateInitialised(true);
    }
  }, [filters, defaultDateInitialised, granularityParam, activePage]);

  // Image Snapshot supports only a single date (quarter) selection. Collapse any
  // multi-value or preset selection carried over from other pages to a single quarter.
  useEffect(() => {
    if (activePage !== "image-snapshot" || !dateRangePerfParam) return;
    const paramStr = Array.isArray(dateRangePerfParam) ? dateRangePerfParam.join(",") : String(dateRangePerfParam);

    // Already a single concrete quarter (e.g. "Q2 FY 2024") — nothing to do.
    if (!paramStr.includes(",") && !paramStr.startsWith("Last ")) return;

    // Rank a "Qn FY YYYY" value chronologically (later year, then later quarter) so we can
    // pick the most recent one regardless of the order the API returns them in.
    const rankQuarter = (q: string) => {
      const year = parseInt(q.match(/\b(?:FY|CY)\s*(\d+)/)?.[1] ?? "0", 10);
      const unit = parseInt(q.match(/Q(\d+)/)?.[1] ?? "0", 10);
      return year * 100 + unit;
    };
    const latestOf = (quarters: string[]) =>
      quarters.reduce<string | undefined>((latest, cur) => (latest && rankQuarter(latest) >= rankQuarter(cur) ? latest : cur), undefined);

    // Multi-value carried over from another page — keep the latest selected quarter.
    if (paramStr.includes(",")) {
      const latest = latestOf(paramStr.split(","));
      if (latest) dispatch(setSpecificParameter("date_range_brand_perf", latest));
      return;
    }

    // Preset (e.g. "Last 6 quarters") — fall back to the latest available quarter.
    const allQuarters = (groupedDateRangeData ?? []).flatMap((val: { year: string; child?: Array<Record<string, string>> }) =>
      (val.child ?? []).map((c) => `${c.quarter ?? c.month} ${val.year}`)
    );
    const latest = latestOf(allQuarters);
    if (latest) dispatch(setSpecificParameter("date_range_brand_perf", latest));
  }, [activePage, dateRangePerfParam, groupedDateRangeData]);

  //reset competitor on model change
  useEffect(() => {
    dispatch(setSpecificParameter("competitor", ALL_OPTION_NO_SPACE));
  }, [modelParam]);

  const resetFilters = React.useCallback(() => {
    eventTracking(MixpanelEvents.filter_reset, { dashboard: "Brand Performance", page: performancePageParam });

    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(DEFAULT_BRAND_PERFORMANCE_PARAMETERS)) {
      params.set(key, value);
    }

    history.push({ search: `?${params.toString()}` });
    window.location.reload();
  }, [history]);

  const handlebrandKPIFilterSubmission = (evt: React.MouseEvent<HTMLElement>) => {
    const {
      currentTarget: { dataset },
    } = evt;
    const filterName = dataset?.filter;
    const optionValue = dataset?.value;

    if (filterName && optionValue && setBrandKPIMaxRangeScore) {
      setBrandKPIMaxRangeScore(Number(optionValue));
    }
    closeFilterOptions();
    setShowFilterOptions(false);
  };

  const handleNIMRankingFilterSubmission = (evt: React.MouseEvent<HTMLElement>) => {
    const {
      currentTarget: { dataset },
    } = evt;
    const filterName = dataset?.filter;
    const optionValue = dataset?.value;

    if (filterName && optionValue && setNimLatestRanking) {
      setNimLatestRanking(Number(optionValue));
    }
    closeFilterOptions();
    setShowFilterOptions(false);
  };

  const handleFilterOptionClick = (evt: React.MouseEvent<HTMLElement>) => {
    const {
      currentTarget: { dataset },
    } = evt;
    const filterName = dataset?.filter;
    const optionValue = dataset?.value;

    if (filterName && optionValue) {
      if (filterName == "attribute") {
        dispatch(setSpecificParameter(filterName, encodeURIComponent(optionValue)));
      } else {
        dispatch(setSpecificParameter(filterName, optionValue));
      }
      eventTracking(MixpanelEvents.filter_change, {
        filter: filterName,
        value: optionValue,
        dashboard: "Brand Performance",
        page: performancePageParam,
      });
    }
    closeFilterOptions();
    setShowFilterOptions(false);
  };

  const showFilterOptionsFn = (evt: React.MouseEvent<HTMLElement>) => {
    const {
      currentTarget: { dataset },
    } = evt;
    const optionsId = dataset?.optionsUl as string;
    const shownClass = "showOptions";

    const optionsElement = document.getElementById(optionsId) as HTMLElement;

    if (showFilterOptions && optionsElement.classList.contains(shownClass)) {
      closeFilterOptions();
      setShowFilterOptions(false);
    } else {
      closeFilterOptions();
      optionsElement?.classList.add(shownClass);
      setShowFilterOptions(true);
    }
  };

  const handleCompetitorFilterSubmission = useCallback(() => {
    const parameterName = "competitor";
    const selectedCheckboxes = document.querySelectorAll(
      `[type="checkbox"][data-filter-name="${parameterName}"][data-options-type="single"]:checked`
    ) as NodeListOf<HTMLInputElement>;

    const selectedValues: Array<string> = [];
    selectedCheckboxes?.forEach((checkbox) => selectedValues.push(checkbox.value));

    closeFilterOptions();
    dispatch(setSpecificParameter("competitor", selectedValues.join(",")));
  }, [filters?.competitor]);

  const handleModelFilterSubmission = useCallback(() => {
    const parameterName = "model";
    const selectedCheckboxes = document.querySelectorAll(
      `[type="checkbox"][data-filter-name="${parameterName}"][data-options-type="single"]:checked`
    ) as NodeListOf<HTMLInputElement>;

    const selectedValues: Array<string> = [];
    selectedCheckboxes?.forEach((checkbox) => selectedValues.push(checkbox.value));

    closeFilterOptions();
    dispatch(setSpecificParameter("model", selectedValues.join(",")));
  }, [filters?.model]);

  const handleMarketFilterSubmission = useCallback(() => {
    const parameterName = "market";
    const selectedCheckboxes = document.querySelectorAll(
      `[type="checkbox"][data-filter-name="${parameterName}"][data-options-type="single"]:checked`
    ) as NodeListOf<HTMLInputElement>;

    const selectedValues: Array<string> = [];
    selectedCheckboxes?.forEach((checkbox) => selectedValues.push(checkbox.value));
    closeFilterOptions();
    dispatch(setSpecificParameter("market", selectedValues.join(",")));
  }, [filters?.market]);

  const handleSegmentFilterSubmission = useCallback(() => {
    const parameterName = "segment";
    const selectedCheckboxes = document.querySelectorAll(
      `[type="checkbox"][data-filter-name="${parameterName}"][data-options-type="single"]:checked`
    ) as NodeListOf<HTMLInputElement>;

    const selectedValues: Array<string> = [];
    selectedCheckboxes?.forEach((checkbox) => selectedValues.push(checkbox.value));

    closeFilterOptions();
    dispatch(setSpecificParameter("segment", selectedValues.join(",")));
  }, [filters?.segment]);

  const handleDataCategoryFilterSubmission = useCallback(() => {
    const parameterName = "data_category";
    const selectedCheckboxes = document.querySelectorAll(
      `[type="checkbox"][data-filter-name="${parameterName}"][data-options-type="single"]:checked`
    ) as NodeListOf<HTMLInputElement>;

    const selectedValues: Array<string> = [];
    selectedCheckboxes?.forEach((checkbox) => selectedValues.push(checkbox.value));

    closeFilterOptions();
    dispatch(setSpecificParameter("data_category", selectedValues.join(",")));
  }, [filters?.data_category]);

  const handleAnnualMileageFilterSubmission = useCallback(() => {
    const parameterName = "annual_mileage";
    const selectedCheckboxes = document.querySelectorAll(
      `[type="checkbox"][data-filter-name="${parameterName}"][data-options-type="single"]:checked`
    ) as NodeListOf<HTMLInputElement>;

    const selectedValues: Array<string> = [];
    selectedCheckboxes?.forEach((checkbox) => selectedValues.push(checkbox.value));

    closeFilterOptions();
    dispatch(setSpecificParameter("annual_mileage", selectedValues.join(",")));
  }, [filters?.annual_mileage]);

  const handleMoFilterSubmission = useCallback(() => {
    const parameterName = "mo";
    const selectedCheckboxes = document.querySelectorAll(
      `[type="checkbox"][data-filter-name="${parameterName}"][data-options-type="single"]:checked`
    ) as NodeListOf<HTMLInputElement>;

    const selectedValues: Array<string> = [];
    selectedCheckboxes?.forEach((checkbox) => selectedValues.push(checkbox.value));

    closeFilterOptions();
    dispatch(setSpecificParameter("mo", selectedValues.join(",")));
  }, [filters?.mo]);

  const handleModelYearFilterSubmission = useCallback(() => {
    const parameterName = "model_year";
    const selectedCheckboxes = document.querySelectorAll(
      `[type="checkbox"][data-filter-name="${parameterName}"][data-options-type="single"]:checked`
    ) as NodeListOf<HTMLInputElement>;

    const selectedValues: Array<string> = [];
    selectedCheckboxes?.forEach((checkbox) => selectedValues.push(checkbox.value));

    closeFilterOptions();
    dispatch(setSpecificParameter("model_year", selectedValues.join(",")));
  }, [filters?.model_year]);

  const handleDateFilterSubmission = () => {
    const parameterName = "date_range_brand_perf";
    const selectedCheckboxes = document.querySelectorAll(
      `[type="checkbox"][data-filter-name="${parameterName}"][data-parent-value]:checked`
    ) as NodeListOf<HTMLInputElement>;

    const selectedValues: Array<string> = [];
    selectedCheckboxes?.forEach((checkbox) => {
      if (!/^\d+$/.test(checkbox.value) && checkbox.value != "All") {
        // Convert CY back to FY before storing (for API payload)
        const valueToStore = checkbox.value.replace(/\bCY\b/g, "FY");
        selectedValues.push(valueToStore);
      }
    });

    closeFilterOptions();
    const value = selectedValues.join(",");
    dispatch(setSpecificParameter(parameterName, value));
    eventTracking(MixpanelEvents.filter_change, {
      filter: parameterName,
      value,
      dashboard: "Brand Performance",
      page: performancePageParam,
    });
  };

  // Image Snapshot uses a single-select (one quarter) date picker. Shape the grouped
  // year -> quarter/month data into the nested single-select filter's options.
  const singleSelectDateOptions = useMemo(
    () =>
      (displayDateRangeData ?? []).map((item: { year: string; child?: Array<Record<string, string>> }) => ({
        value: item.year,
        region: item.year,
        children: (item.child ?? []).map((childItem) => ({ value: `${childItem.quarter ?? childItem.month} ${item.year}` })),
      })),
    [displayDateRangeData]
  );

  const handleSingleDateSelect = useCallback(
    (value: string) => {
      // Convert CY back to FY before storing (for API payload), matching handleDateFilterSubmission.
      const valueToStore = value.replace(/\bCY\b/g, "FY");
      dispatch(setSpecificParameter("date_range_brand_perf", valueToStore));
      eventTracking(MixpanelEvents.filter_change, {
        filter: "date_range_brand_perf",
        value: valueToStore,
        dashboard: "Brand Performance",
        page: performancePageParam,
      });
    },
    [dispatch, performancePageParam]
  );

  const handleAttributeFilterSubmission = useCallback(() => {
    const parameterName = "attribute";
    const selectedCheckboxes = document.querySelectorAll(
      `[type="checkbox"][data-filter-name="${parameterName}"][data-options-type="single"]:checked`
    ) as NodeListOf<HTMLInputElement>;

    const selectedValues: Array<string> = [];
    selectedCheckboxes?.forEach((checkbox) => selectedValues.push(encodeURIComponent(checkbox.value)));

    closeFilterOptions();
    dispatch(setSpecificParameter("attribute", selectedValues.join(",")));
  }, [filters?.attribute]);

  useEffect(() => {
    const selectedLength = competitorParam?.split(",")?.length;
    const val =
      selectedLength == filters?.competitor?.length
        ? ALL_OPTION_NO_SPACE
        : selectedLength == 1
          ? competitorParam
          : `${selectedLength} competitors selected`;
    setCompetitorValue(val);
    eventTracking(MixpanelEvents.filter_change, {
      filter: "competitor",
      value: competitorParam?.split(","),
      dashboard: "Brand Performance",
      page: performancePageParam,
    });
  }, [filters?.competitor, competitorParam]);

  useEffect(() => {
    const selectedLength = segmentParam?.split(",")?.length;
    const val =
      selectedLength == filters?.segment?.length
        ? ALL_OPTION_NO_SPACE
        : selectedLength == 1
          ? segmentParam
          : `${selectedLength} segments selected`;
    setSegmentValue(val);
  }, [filters?.segment, segmentParam]);

  useEffect(() => {
    const selectedLength = annualMilageParam?.split(",")?.length;
    const val =
      selectedLength == filters?.annual_mileage?.length
        ? ALL_OPTION_NO_SPACE
        : selectedLength == 1
          ? annualMilageParam
          : `${selectedLength} mileages selected`;
    setAnnualMileageValue(val);
  }, [filters?.annual_mileage, annualMilageParam]);

  useEffect(() => {
    const selectedLength = dataCategoryParam?.split(",")?.length;
    const val =
      selectedLength == filters?.data_category?.length
        ? ALL_OPTION_NO_SPACE
        : selectedLength == 1
          ? dataCategoryParam
          : `${selectedLength} data categories selected`;
    setDataCategoryValue(val);
  }, [filters?.data_category, dataCategoryParam]);

  useEffect(() => {
    // if (
    //   activePage == "monthly-table-for-download" ||
    //   activePage == "yearly-table-for-download" ||
    //   activePage == "quarterly-table-for-download"
    // ) {
    const selectedLength = modelParam?.split(",")?.length;
    const val =
      selectedLength == filters?.model?.length
        ? ALL_OPTION_NO_SPACE
        : selectedLength == 1
          ? modelParam
          : `${selectedLength} models selected`;
    setModelValue(val);
    // }
  }, [filters?.model, modelParam, dataCategoryParam, marketParam]);

  useEffect(() => {
    const selectedLength = marketParam?.split(",")?.length;
    const val =
      selectedLength == filters?.market?.length
        ? ALL_OPTION_NO_SPACE
        : selectedLength == 1
          ? marketParam
          : `${selectedLength} markets selected`;
    setMarketValue(val);
  }, [filters?.market, marketParam]);

  useEffect(() => {
    const selectedLength = moParam?.split(",")?.length;
    const val =
      selectedLength == filters?.mo?.length ? ALL_OPTION_NO_SPACE : selectedLength == 1 ? moParam : `${selectedLength} mos selected`;
    setMoValue(val);
  }, [filters?.mo, moParam]);

  useEffect(() => {
    const selectedLength = modelYearParam?.split(",")?.length;
    const val =
      selectedLength == filters?.model_year?.length
        ? ALL_OPTION_NO_SPACE
        : selectedLength == 1
          ? modelYearParam
          : `${selectedLength} model years selected`;
    setModelYearValue(val);
  }, [filters?.model_year, modelYearParam]);

  useEffect(() => {
    const selectedLength = attributeParam?.split(",")?.length;
    const val =
      selectedLength == filters?.attribute?.length
        ? ALL_OPTION_NO_SPACE
        : selectedLength == 1
          ? decodeURIComponent(attributeParam)
          : `${selectedLength} attributes selected`;
    setAttributeValue(val);
  }, [filters?.attribute, attributeParam]);

  const presetOptions = useMemo(() => {
    if (activePage == "monthly-table-for-download" || activePage == "rv-table-for-download") {
      return ["Last 12 months"];
    }
    if (availableGranularities.includes("quarterly")) {
      return ["Last 6 quarters"];
    }
    return undefined;
  }, [availableGranularities, activePage]);

  useEffect(() => {
    if (
      activePage == "monthly-table-for-download" ||
      activePage == "yearly-table-for-download" ||
      activePage == "quarterly-table-for-download" ||
      activePage == "rv-table-for-download"
    )
      dispatch(setSpecificParameter("market", ALL_OPTION_NO_SPACE));
  }, [activePage]);

  // This effect ensures we always update the filters to match the current model coming from
  // the backend instead of always using Brand as a placeholder default
  useEffect(() => {
    if (
      filters?.model?.length &&
      (activePage === "monthly-table-for-download" ||
        activePage === "yearly-table-for-download" ||
        activePage === "quarterly-table-for-download")
    ) {
      // If current modelParam is not in the new filters, update it
      if (!modelParam.split(",").some((model: string) => filters.model.includes(model))) {
        dispatch(setSpecificParameter("model", filters.model[0]));
      }
    }
  }, [filters?.model, activePage]);

  const isMonthlyDownload = activePage === "monthly-table-for-download";

  return (
    <FiltersLayout resetFilters={resetFilters} extraClass={"digital_filters"}>
      <>
        {activePage == "nim-latest" || activePage == "brand-kpi-summary" ? null : activePage == "image-snapshot" ? (
          <ErrorBoundary fallback={<ErrorMsg />}>
            {/* Image Snapshot supports a single date (quarter) selection, applied on click. */}
            <InAppSingleSelectNestedFilter
              options={singleSelectDateOptions}
              filterName={"date_range_brand_perf"}
              filterLabel={"DATE RANGE"}
              filterValue={convertDisplayLabel(dateRangePerfParam)}
              setValue={handleSingleDateSelect as Dispatch<React.SetStateAction<string>>}
            />
          </ErrorBoundary>
        ) : activePage == "oao" ||
          activePage == "image-attributes" ||
          activePage == "brand-health-index" ||
          activePage == "buzz-tracking" ||
          activePage == "market-share-by-channel" ||
          activePage == "brand-kpi-trend" ||
          activePage == "nim" ||
          activePage == "purchase-funnel" ||
          activePage == "tech-awareness" ||
          activePage == "monthly-table-for-download" ||
          activePage == "yearly-table-for-download" ||
          activePage == "quarterly-table-for-download" ||
          activePage == "rv-table-for-download" ||
          activePage == "rv" ? (
          <ErrorBoundary fallback={<ErrorMsg />}>
            <MultiSelectFilter
              parentKey="year"
              // @ts-ignore
              filterList={displayDateRangeData}
              filterName="DATE RANGE"
              childKey={filters?.fiscal_year && "months" in filters.fiscal_year[0] ? "month" : "quarter"}
              value={isMonthlyDownload ? stripFY(dateRangePerfParam) : convertDisplayLabel(dateRangePerfParam)}
              parameterName={"date_range_brand_perf"}
              parameterValue={isMonthlyDownload ? dateRangePerfParam : convertDisplayLabel(dateRangePerfParam)}
              onShowFilterOptions={showFilterOptionsFn}
              handleFilterSubmission={handleDateFilterSubmission}
              presetOptions={presetOptions}
              labelTransform={isMonthlyDownload ? stripFY : undefined}
            />
          </ErrorBoundary>
        ) : (
          <ErrorBoundary fallback={<ErrorMsg />}>
            <PurchaseFunnelDateFilter
              value={dateRangeParam}
              handleFilterOptionClick={handleFilterOptionClick}
              onFilterClick={showFilterOptionsFn}
              presetDatesList={DEFAULT_BRAND_PERFORMANCE_DATE_PARAMS}
              latestDate={lastRefreshDate}
              minDateValue={filters?.date_range}
            />
          </ErrorBoundary>
        )}
        <ErrorBoundary fallback={<ErrorMsg />}>
          <DefaultFilter
            filterName={"brand"}
            list={filters?.brand ?? []}
            filterValue={brandParam}
            filterLabel={"BRAND"}
            handleFilterOptionClick={handleFilterOptionClick}
          />
        </ErrorBoundary>
        {activePage == "brand-kpi-summary" || activePage == "nim-latest" ? null : activePage == "monthly-table-for-download" ||
          activePage == "yearly-table-for-download" ||
          activePage == "quarterly-table-for-download" ||
          activePage == "rv-table-for-download" ? (
          <ErrorBoundary fallback={<ErrorMsg />}>
            <MultiSelectFilter
              parentKey="market"
              // @ts-ignore
              filterList={filters?.market ?? []}
              filterName="COUNTRY"
              value={marketValue}
              parameterName={"market"}
              parameterValue={marketParam}
              onShowFilterOptions={showFilterOptionsFn}
              handleFilterSubmission={handleMarketFilterSubmission}
            />
          </ErrorBoundary>
        ) : (
          <ErrorBoundary fallback={<ErrorMsg />}>
            <DefaultFilter
              filterName={"market"}
              list={filters?.market ?? []}
              filterValue={marketParam}
              filterLabel={"COUNTRY"}
              handleFilterOptionClick={handleFilterOptionClick}
            />
          </ErrorBoundary>
        )}
        {(activePage === "image-attributes" || activePage === "purchase-funnel" || activePage === "image-snapshot" || activePage === "quarterly-table-for-download") && isUSMarket && (
          <ErrorBoundary fallback={<ErrorMsg />}>
            <DefaultFilter
              filterName={"data_source"}
              list={filters?.data_source?.length ? filters.data_source : ["yougov"]}
              filterValue={dataSourceParam}
              filterLabel={"DATA SOURCE"}
              handleFilterOptionClick={handleFilterOptionClick}
              labelMap={{ yougov: "YouGov" }}
            />
          </ErrorBoundary>
        )}
        {activePage == "brand-kpi-summary" && (
          <ErrorBoundary fallback={<ErrorMsg />}>
            <DefaultFilter
              filterName={"brand_kpi"}
              list={filters?.brand_kpi ?? []}
              filterValue={brandKpiParam}
              filterLabel={"BRAND KPI"}
              handleFilterOptionClick={handleFilterOptionClick}
            />
          </ErrorBoundary>
        )}

        {activePage == "brand-kpi-trend" ? null : activePage == "segment-volume-tiv" ||
          activePage == "brand-kpi-summary" ||
          activePage == "nim-latest" ||
          activePage == "nim" ||
          activePage == "tech-awareness" ? null : activePage == "monthly-table-for-download" ||
            activePage == "yearly-table-for-download" ||
            activePage == "quarterly-table-for-download" ? (
          <ErrorBoundary fallback={<ErrorMsg />}>
            <MultiSelectFilter
              parentKey="model"
              // @ts-ignore
              filterList={filters?.model ?? []}
              filterName="NISSAN BRAND/MODEL"
              value={modelValue}
              parameterName={"model"}
              parameterValue={modelParam}
              onShowFilterOptions={showFilterOptionsFn}
              handleFilterSubmission={handleModelFilterSubmission}
            />
          </ErrorBoundary>
        ) : (
          <>
            <ErrorBoundary fallback={<ErrorMsg />}>
              <DefaultFilter
                filterName={"model"}
                list={filters?.model ?? []}
                filterValue={modelParam}
                filterLabel={"NISSAN BRAND/MODEL"}
                handleFilterOptionClick={handleFilterOptionClick}
              />
            </ErrorBoundary>
          </>
        )}
        {activePage == "quarterly-table-for-download" && (
          <ErrorBoundary fallback={<ErrorMsg />}>
            <DefaultFilter
              filterName={"select_value"}
              list={filters?.select_value ?? ["Score (%)", "Sample Size", "Rank (12m rolling only)"]}
              filterValue={selectValueParam}
              filterLabel={"SELECT VALUE"}
              handleFilterOptionClick={handleFilterOptionClick}
            />
          </ErrorBoundary>
        )}
        {activePage == "monthly-table-for-download" ||
          activePage == "yearly-table-for-download" ||
          activePage == "quarterly-table-for-download" ? (
          <ErrorBoundary fallback={<ErrorMsg />}>
            <MultiSelectFilter
              parentKey="data-category"
              // @ts-ignore
              filterList={filters?.data_category ?? []}
              filterName="DATA CATEGORY"
              value={dataCategoryValue}
              parameterName={"data_category"}
              parameterValue={dataCategoryParam}
              onShowFilterOptions={showFilterOptionsFn}
              handleFilterSubmission={handleDataCategoryFilterSubmission}
            />
          </ErrorBoundary>
        ) : null}
        {activePage == "segment-volume-tiv" ||
          activePage == "tp" ||
          activePage == "brand-kpi-trend" ||
          activePage == "brand-kpi-summary" ||
          activePage == "nim-latest" ||
          activePage == "rv" ||
          activePage == "tpva" ||
          activePage == "tech-awareness" ||
          ((activePage == "image-attributes" || activePage == "image-snapshot") && isUSMarket && dataSourceParam === "yougov") ? null : activePage == "purchase-funnel" ? (
            <ErrorBoundary fallback={<ErrorMsg />}>
              <DefaultFilter
                filterName={"competitor"}
                list={filters?.competitor ?? []}
                filterValue={competitorParam}
                filterLabel={"COMPETITOR"}
                handleFilterOptionClick={handleFilterOptionClick}
              />
            </ErrorBoundary>
          ) : (
          <ErrorBoundary fallback={<ErrorMsg />}>
            <MultiSelectFilter
              parentKey="competitor"
              // @ts-ignore
              filterList={filters?.competitor ?? []}
              filterName="COMPETITOR"
              value={competitorValue}
              parameterName={"competitor"}
              parameterValue={competitorParam}
              onShowFilterOptions={showFilterOptionsFn}
              handleFilterSubmission={handleCompetitorFilterSubmission}
            />
          </ErrorBoundary>
        )}

        {activePage === "oao" ||
          activePage === "brand-health-index" ||
          activePage === "market-share-by-channel" ||
          activePage == "brand-kpi-trend" ||
          activePage == "brand-kpi-summary" ||
          activePage == "nim" ||
          activePage == "image-snapshot" ||
          activePage == "nim-latest" ||
          activePage == "quarterly-table-for-download" ||
          activePage == "monthly-table-for-download" ||
          activePage == "purchase-funnel" ? (
          <ErrorBoundary fallback={<ErrorMsg />}>
            <DefaultFilter
              filterName={"category"}
              list={filters?.category ?? []}
              filterValue={categoryParam}
              filterLabel={"CATEGORY DETAIL"}
              handleFilterOptionClick={handleFilterOptionClick}
            />
          </ErrorBoundary>
        ) : null}

        {activePage === "image-attributes" || activePage === "buzz-tracking" || activePage == "nim" ? (
          <ErrorBoundary fallback={<ErrorMsg />}>
            <DefaultFilter
              filterName={"sub_category"}
              list={filters?.sub_category ?? []}
              filterValue={subCategoryParam}
              filterLabel={"SUB CATEGORY"}
              handleFilterOptionClick={handleFilterOptionClick}
            />
          </ErrorBoundary>
        ) : null}
        {activePage == "quarterly-table-for-download" && (dataCategoryParam == "Image" || dataCategoryParam == "NIM") ? (
          <ErrorBoundary>
            <MultiSelectFilter
              parentKey="attribute"
              // @ts-ignore
              filterList={filters?.attribute ?? []}
              filterName="ATTRIBUTE"
              value={attributeValue}
              parameterName={"attribute"}
              parameterValue={attributeParam}
              onShowFilterOptions={showFilterOptionsFn}
              handleFilterSubmission={handleAttributeFilterSubmission}
            />
          </ErrorBoundary>
        ) : activePage === "image-attributes" ||
          activePage === "buzz-tracking" ||
          activePage == "nim" ||
          activePage == "quarterly-table-for-download" ||
          activePage == "nim-latest" ? (
          <ErrorBoundary fallback={<ErrorMsg />}>
            <DefaultFilter
              filterName={"attribute"}
              list={filters?.attribute ?? []}
              filterValue={decodeURIComponent(attributeParam)}
              filterLabel={"ATTRIBUTE"}
              handleFilterOptionClick={handleFilterOptionClick}
            />
          </ErrorBoundary>
        ) : null}

        {activePage === "segment-volume-tiv" ? (
          <>
            <ErrorBoundary fallback={<ErrorMsg />}>
              <MultiSelectFilter
                parentKey="segment"
                // @ts-ignore
                filterList={filters?.segment ?? []}
                filterName="SEGMENT NAME"
                value={segmentValue}
                parameterName={"segment"}
                parameterValue={segmentParam}
                onShowFilterOptions={showFilterOptionsFn}
                handleFilterSubmission={handleSegmentFilterSubmission}
              />
            </ErrorBoundary>
          </>
        ) : null}
        {activePage === "brand-kpi-trend" && (
          <ErrorBoundary fallback={<ErrorMsg />}>
            <BrandKPIMaximumScoreRangeFilter
              filterName={"brand_kpi_max_score"}
              filterValue={String(brandKPIMaxRangeScore)}
              filterLabel={"MAXIMUM SCORE RANGE"}
              list={[100, 90, 80, 70, 60, 50, 40, 30, 20, 10]}
              handleFilterOptionClick={handlebrandKPIFilterSubmission}
            />
          </ErrorBoundary>
        )}

        {activePage === "rv" || activePage === "rv-table-for-download" ? (
          <>
            <ErrorBoundary fallback={<ErrorMsg />}>
              <MultiSelectFilter
                parentKey="annual_mileage"
                // @ts-ignore
                filterList={filters?.annual_mileage ?? []}
                filterName="ANNUAL MILAGE/KM"
                value={annualMileageValue}
                parameterName={"annual_mileage"}
                parameterValue={annualMilageParam}
                onShowFilterOptions={showFilterOptionsFn}
                handleFilterSubmission={handleAnnualMileageFilterSubmission}
              />
            </ErrorBoundary>

            <ErrorBoundary fallback={<ErrorMsg />}>
              <MultiSelectFilter
                parentKey="mo"
                // @ts-ignore
                filterList={filters?.forecast_month ?? []}
                filterName="MO"
                value={moValue}
                parameterName={"mo"}
                parameterValue={moParam}
                onShowFilterOptions={showFilterOptionsFn}
                handleFilterSubmission={handleMoFilterSubmission}
              />
            </ErrorBoundary>

            <ErrorBoundary fallback={<ErrorMsg />}>
              <MultiSelectFilter
                parentKey="model_year"
                // @ts-ignore
                filterList={filters?.model_year ?? []}
                filterName="MODEL YEAR"
                value={modelYearValue}
                parameterName={"model_year"}
                parameterValue={modelYearParam}
                onShowFilterOptions={showFilterOptionsFn}
                handleFilterSubmission={handleModelYearFilterSubmission}
              />
            </ErrorBoundary>
          </>
        ) : null}
        {activePage == "nim-latest" && (
          <ErrorBoundary fallback={<ErrorMsg />}>
            <DefaultFilter
              filterName={"ranking"}
              list={filters?.ranking ?? []}
              filterValue={String(nimLatestRanking)}
              filterLabel={"RANKING"}
              handleFilterOptionClick={handleNIMRankingFilterSubmission}
            />
          </ErrorBoundary>
        )}

        {activePage == "monthly-table-for-download" ||
          activePage == "yearly-table-for-download" ||
          activePage == "quarterly-table-for-download" ||
          activePage == "rv-table-for-download" ||
          activePage == "image-snapshot" ||
          activePage == "brand-kpi-summary"
          ? null
          : granularityOptions}
      </>
    </FiltersLayout>
  );
});

export default BrandPerformanceFilters;
